import React, { useState, useEffect } from 'react';
import { tenantAPI } from '../../services/api';
import { toast } from 'react-toastify';
import {
    CreditCard,
    DollarSign,
    Calendar,
    TrendingUp,
    Users,
    Building2,
    CheckCircle,
    XCircle,
    Clock,
    Edit,
    AlertCircle
} from 'lucide-react';
import './Billing.css';
import DateInput from '../../components/DateInput/DateInput';
import { formatDate } from '../../utils/dateFormat';

const Billing = () => {
    const [tenants, setTenants] = useState([]);
    const [loading, setLoading] = useState(true);
    const [searchTerm, setSearchTerm] = useState('');
    const [filterPlan, setFilterPlan] = useState('all');
    const [filterType, setFilterType] = useState('all');
    const [showModal, setShowModal] = useState(false);
    const [selectedTenant, setSelectedTenant] = useState(null);

    const [subscriptionData, setSubscriptionData] = useState({
        plan: 'basic',
        validFrom: '',
        validTill: '',
        maxBranches: 1,
        maxUsers: 100,
        amount: 0,
        currency: 'INR',
        billingCycle: 'monthly'
    });

    useEffect(() => {
        fetchTenants();
    }, []);

    const fetchTenants = async () => {
        try {
            setLoading(true);
            const response = await tenantAPI.getAll();
            setTenants(response.data.data.tenants || []);
        } catch (error) {
            toast.error('Failed to fetch tenants');
            console.error(error);
        } finally {
            setLoading(false);
        }
    };

    const handleEditSubscription = (tenant) => {
        setSelectedTenant(tenant);
        setSubscriptionData({
            plan: tenant.subscription?.plan || 'basic',
            validFrom: tenant.subscription?.validFrom ? new Date(tenant.subscription.validFrom).toISOString().split('T')[0] : '',
            validTill: tenant.subscription?.validTill ? new Date(tenant.subscription.validTill).toISOString().split('T')[0] : '',
            maxBranches: tenant.subscription?.maxBranches || 1,
            maxUsers: tenant.subscription?.maxUsers || 100,
            amount: tenant.subscription?.amount || 0,
            currency: tenant.subscription?.currency || 'INR',
            billingCycle: tenant.subscription?.billingCycle || 'monthly'
        });
        setShowModal(true);
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        if (!selectedTenant) return;

        try {
            await tenantAPI.update(selectedTenant._id, {
                subscription: subscriptionData
            });
            toast.success('Subscription updated successfully');
            setShowModal(false);
            setSelectedTenant(null);
            fetchTenants();
        } catch (error) {
            toast.error('Failed to update subscription');
            console.error(error);
        }
    };

    const filteredTenants = tenants.filter(tenant => {
        const matchesSearch =
            tenant.name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
            tenant.companyName?.toLowerCase().includes(searchTerm.toLowerCase());

        const matchesPlan = filterPlan === 'all' || tenant.subscription?.plan === filterPlan;
        const matchesType = filterType === 'all' || tenant.type === filterType;

        return matchesSearch && matchesPlan && matchesType;
    });

    // Calculate total revenue
    const totalRevenue = tenants.reduce((sum, tenant) => sum + (tenant.subscription?.amount || 0), 0);
    const activeSubscriptions = tenants.filter(t =>
        t.status === 'active' &&
        t.subscription?.validTill &&
        new Date(t.subscription.validTill) > new Date()
    ).length;

    // Plan configuration
    const planColors = {
        free: 'gray',
        basic: 'blue',
        pro: 'purple',
        enterprise: 'pink'
    };

    const planConfig = {
        free: { price: 0, users: 50, branches: 1 },
        basic: { price: 999, users: 100, branches: 3 },
        pro: { price: 2999, users: 500, branches: 10 },
        enterprise: { price: 9999, users: -1, branches: -1 }
    };

    // Organization type config
    const orgTypeConfig = {
        hotel: { icon: 'fa-hotel', label: 'Hotel', color: '#f59e0b' },
        isp: { icon: 'fa-wifi', label: 'ISP', color: '#3b82f6' },
        mall: { icon: 'fa-store', label: 'Mall', color: '#ec4899' },
        campus: { icon: 'fa-graduation-cap', label: 'Campus', color: '#10b981' },
        enterprise: { icon: 'fa-building', label: 'Enterprise', color: '#8b5cf6' },
        other: { icon: 'fa-globe', label: 'Other', color: '#6b7280' }
    };

    return (
        <div className="billing-page">
            {/* Header */}
            <div className="page-header-modern">
                <div className="header-content">
                    <div className="header-text">
                        <h1 className="page-title-modern">
                            <CreditCard className="title-icon" />
                            Billing & Subscriptions
                        </h1>
                        <p className="page-subtitle-modern">
                            Manage tenant subscriptions and billing information
                        </p>
                    </div>
                </div>
            </div>

            {/* Stats Grid */}
            <div className="stats-grid">
                <div className="stat-card">
                    <div className="stat-icon green">
                        <DollarSign size={24} />
                    </div>
                    <div className="stat-details">
                        <div className="stat-value">₹{totalRevenue.toLocaleString()}</div>
                        <div className="stat-label">Total Revenue</div>
                    </div>
                </div>

                <div className="stat-card">
                    <div className="stat-icon blue">
                        <CheckCircle size={24} />
                    </div>
                    <div className="stat-details">
                        <div className="stat-value">{activeSubscriptions}</div>
                        <div className="stat-label">Active Subscriptions</div>
                    </div>
                </div>

                <div className="stat-card">
                    <div className="stat-icon purple">
                        <Building2 size={24} />
                    </div>
                    <div className="stat-details">
                        <div className="stat-value">{tenants.length}</div>
                        <div className="stat-label">Total Tenants</div>
                    </div>
                </div>

                <div className="stat-card">
                    <div className="stat-icon pink">
                        <TrendingUp size={24} />
                    </div>
                    <div className="stat-details">
                        <div className="stat-value">
                            {tenants.filter(t => t.subscription?.plan === 'enterprise').length}
                        </div>
                        <div className="stat-label">Enterprise Plans</div>
                    </div>
                </div>
            </div>

            {/* Filters */}
            <div className="filters-section">
                <div className="search-box-modern">
                    <i className="fas fa-search"></i>
                    <input
                        type="text"
                        placeholder="Search tenants by name or company..."
                        value={searchTerm}
                        onChange={(e) => setSearchTerm(e.target.value)}
                    />
                </div>

                <select
                    value={filterPlan}
                    onChange={(e) => setFilterPlan(e.target.value)}
                    className="filter-select-modern"
                >
                    <option value="all">All Plans</option>
                    <option value="free">Free</option>
                    <option value="basic">Basic</option>
                    <option value="pro">Pro</option>
                    <option value="enterprise">Enterprise</option>
                </select>

                <select
                    value={filterType}
                    onChange={(e) => setFilterType(e.target.value)}
                    className="filter-select-modern"
                >
                    <option value="all">All Types</option>
                    <option value="hotel">🏨 Hotel</option>
                    <option value="isp">📡 ISP</option>
                    <option value="mall">🏬 Mall</option>
                    <option value="campus">🎓 Campus</option>
                    <option value="enterprise">🏢 Enterprise</option>
                    <option value="other">🌐 Other</option>
                </select>
            </div>

            {/* Billing Grid */}
            {loading ? (
                <div className="loading-modern">
                    <div className="spinner-modern"></div>
                    <p>Loading billing information...</p>
                </div>
            ) : filteredTenants.length === 0 ? (
                <div className="empty-state-modern">
                    <div className="empty-icon">
                        <CreditCard size={48} />
                    </div>
                    <h3>No Tenants Found</h3>
                    <p>No tenants match your search criteria.</p>
                </div>
            ) : (
                <div className="billing-grid">
                    {filteredTenants.map((tenant) => {
                        const subscription = tenant.subscription || {};
                        const isExpired = subscription.validTill && new Date(subscription.validTill) < new Date();
                        const daysLeft = subscription.validTill
                            ? Math.ceil((new Date(subscription.validTill) - new Date()) / (1000 * 60 * 60 * 24))
                            : 0;

                        return (
                            <div key={tenant._id} className="billing-card">
                                {/* Card Header */}
                                <div className="billing-card-header">
                                    <div className="tenant-info">
                                        <div className="tenant-avatar">
                                            <Building2 size={24} />
                                        </div>
                                        <div>
                                            <h3 className="tenant-name">{tenant.name}</h3>
                                            <p className="tenant-company">{tenant.companyName || 'N/A'}</p>
                                            <span className="type-badge" style={{ background: `${orgTypeConfig[tenant.type || 'enterprise']?.color}15`, color: orgTypeConfig[tenant.type || 'enterprise']?.color, border: `1px solid ${orgTypeConfig[tenant.type || 'enterprise']?.color}30`, fontSize: '0.65rem', padding: '0.15rem 0.5rem', marginTop: '0.25rem', display: 'inline-flex' }}>
                                                <i className={`fas ${orgTypeConfig[tenant.type || 'enterprise']?.icon}`} style={{ fontSize: '0.55rem' }}></i>
                                                {orgTypeConfig[tenant.type || 'enterprise']?.label}
                                            </span>
                                        </div>
                                    </div>
                                    <span className={`plan-badge ${planColors[subscription.plan || 'free']}`}>
                                        {(subscription.plan || 'free').toUpperCase()}
                                    </span>
                                </div>

                                {/* Card Body */}
                                <div className="billing-card-body">
                                    <div className="billing-detail">
                                        <div className="detail-icon">
                                            <DollarSign size={18} />
                                        </div>
                                        <div>
                                            <span className="detail-label">Amount</span>
                                            <span className="detail-value">
                                                ₹{subscription.amount || 0} / {subscription.billingCycle || 'month'}
                                            </span>
                                        </div>
                                    </div>

                                    <div className="billing-detail">
                                        <div className="detail-icon">
                                            <Calendar size={18} />
                                        </div>
                                        <div>
                                            <span className="detail-label">Valid Till</span>
                                            <span className="detail-value">
                                                {subscription.validTill
                                                    ? formatDate(subscription.validTill)
                                                    : 'Not set'}
                                            </span>
                                        </div>
                                    </div>

                                    <div className="billing-detail">
                                        <div className="detail-icon">
                                            <Users size={18} />
                                        </div>
                                        <div>
                                            <span className="detail-label">Max Users</span>
                                            <span className="detail-value">
                                                {subscription.maxUsers || 0}
                                            </span>
                                        </div>
                                    </div>

                                    <div className="billing-detail">
                                        <div className="detail-icon">
                                            <Building2 size={18} />
                                        </div>
                                        <div>
                                            <span className="detail-label">Max Branches</span>
                                            <span className="detail-value">
                                                {subscription.maxBranches || 0}
                                            </span>
                                        </div>
                                    </div>
                                </div>

                                {/* Card Footer */}
                                <div className="billing-card-footer">
                                    {isExpired ? (
                                        <div className="status-indicator expired">
                                            <XCircle size={16} />
                                            <span>Expired</span>
                                        </div>
                                    ) : daysLeft <= 7 && daysLeft > 0 ? (
                                        <div className="status-indicator warning">
                                            <AlertCircle size={16} />
                                            <span>{daysLeft} days left</span>
                                        </div>
                                    ) : (
                                        <div className="status-indicator active">
                                            <CheckCircle size={16} />
                                            <span>Active</span>
                                        </div>
                                    )}

                                    <button
                                        onClick={() => handleEditSubscription(tenant)}
                                        className="btn-edit-subscription"
                                    >
                                        <Edit size={16} />
                                        Edit Subscription
                                    </button>
                                </div>
                            </div>
                        );
                    })}
                </div>
            )}

            {/* Edit Subscription Modal */}
            {showModal && selectedTenant && (
                <div className="modal-overlay-modern" onClick={() => setShowModal(false)}>
                    <div className="modal-modern modal-large" onClick={(e) => e.stopPropagation()}>
                        <div className="modal-header-modern">
                            <h2>Edit Subscription - {selectedTenant.name}</h2>
                            <button onClick={() => setShowModal(false)} className="modal-close">
                                <i className="fas fa-times"></i>
                            </button>
                        </div>

                        <form onSubmit={handleSubmit}>
                            <div className="modal-body-modern">
                                <div className="form-grid">
                                    {/* Plan */}
                                    <div className="form-group-modern">
                                        <label>Subscription Plan *</label>
                                        <select
                                            value={subscriptionData.plan}
                                            onChange={(e) => setSubscriptionData({ ...subscriptionData, plan: e.target.value })}
                                            required
                                        >
                                            <option value="free">Free</option>
                                            <option value="basic">Basic</option>
                                            <option value="pro">Pro</option>
                                            <option value="enterprise">Enterprise</option>
                                        </select>
                                    </div>

                                    {/* Billing Cycle */}
                                    <div className="form-group-modern">
                                        <label>Billing Cycle *</label>
                                        <select
                                            value={subscriptionData.billingCycle}
                                            onChange={(e) => setSubscriptionData({ ...subscriptionData, billingCycle: e.target.value })}
                                            required
                                        >
                                            <option value="monthly">Monthly</option>
                                            <option value="yearly">Yearly</option>
                                            <option value="life-time">Lifetime</option>
                                        </select>
                                    </div>

                                    {/* Amount */}
                                    <div className="form-group-modern">
                                        <label>Amount *</label>
                                        <input
                                            type="number"
                                            value={subscriptionData.amount}
                                            onChange={(e) => setSubscriptionData({ ...subscriptionData, amount: parseFloat(e.target.value) || 0 })}
                                            placeholder="Enter amount"
                                            required
                                        />
                                    </div>

                                    {/* Currency */}
                                    <div className="form-group-modern">
                                        <label>Currency *</label>
                                        <select
                                            value={subscriptionData.currency}
                                            onChange={(e) => setSubscriptionData({ ...subscriptionData, currency: e.target.value })}
                                            required
                                        >
                                            <option value="INR">INR (₹)</option>
                                            <option value="USD">USD ($)</option>
                                            <option value="EUR">EUR (€)</option>
                                        </select>
                                    </div>

                                    {/* Valid From */}
                                    <div className="form-group-modern">
                                        <label>Valid From *</label>
                                        <DateInput
                                            type="date"
                                            value={subscriptionData.validFrom}
                                            onChange={(e) => setSubscriptionData({ ...subscriptionData, validFrom: e.target.value })}
                                            required
                                        />
                                    </div>

                                    {/* Valid Till */}
                                    <div className="form-group-modern">
                                        <label>Valid Till *</label>
                                        <DateInput
                                            type="date"
                                            value={subscriptionData.validTill}
                                            onChange={(e) => setSubscriptionData({ ...subscriptionData, validTill: e.target.value })}
                                            required
                                        />
                                    </div>

                                    {/* Max Users */}
                                    <div className="form-group-modern">
                                        <label>Max Users *</label>
                                        <input
                                            type="number"
                                            value={subscriptionData.maxUsers}
                                            onChange={(e) => setSubscriptionData({ ...subscriptionData, maxUsers: parseInt(e.target.value) || 0 })}
                                            placeholder="Maximum users"
                                            required
                                        />
                                    </div>

                                    {/* Max Branches */}
                                    <div className="form-group-modern">
                                        <label>Max Branches *</label>
                                        <input
                                            type="number"
                                            value={subscriptionData.maxBranches}
                                            onChange={(e) => setSubscriptionData({ ...subscriptionData, maxBranches: parseInt(e.target.value) || 0 })}
                                            placeholder="Maximum branches"
                                            required
                                        />
                                    </div>
                                </div>
                            </div>

                            <div className="modal-footer-modern">
                                <button type="button" onClick={() => setShowModal(false)} className="btn-secondary-modern">
                                    Cancel
                                </button>
                                <button type="submit" className="btn-primary-modern">
                                    <CheckCircle size={20} />
                                    Update Subscription
                                </button>
                            </div>
                        </form>
                    </div>
                </div>
            )}
        </div>
    );
};

export default Billing;
